import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency } from '@/lib/utils';
import type { OfferLineFormData } from '@/types/forms/leads';
import { AlertTriangle, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface OfferLinesEditorProps {
    lines: OfferLineFormData[];
    onChange: (lines: OfferLineFormData[]) => void;
    disabled?: boolean;
}

export function emptyOfferLine(): OfferLineFormData {
    return { code: '', name: '', quantity: '1', unit: '', discount: '', unit_price: '', line_total: '' };
}

export function offerLinesTotal(lines: OfferLineFormData[]): number {
    return lines.reduce((sum, line) => sum + (Number(line.line_total) || 0), 0);
}

/**
 * The offer's sell-side table, shared by the create and edit modals and reused
 * read-only in the details view.
 */
export default function OfferLinesEditor({ lines, onChange, disabled = false }: OfferLinesEditorProps) {
    const { t } = useTranslations();

    // Mobile cards keep code and discount folded away — they are empty on most
    // lines. An explicit choice wins; otherwise a line that already carries
    // either value opens itself, so imported data is never hidden.
    const [expandedLines, setExpandedLines] = useState<Record<number, boolean>>({});

    const isLineExpanded = (index: number, line: OfferLineFormData) => expandedLines[index] ?? Boolean(line.code || line.discount);

    const toggleLine = (index: number, line: OfferLineFormData) =>
        setExpandedLines((current) => ({ ...current, [index]: !isLineExpanded(index, line) }));

    const updateLine = (index: number, field: keyof OfferLineFormData, value: string) => {
        onChange(
            lines.map((line, i) => {
                if (i !== index) {
                    return line;
                }

                const next: OfferLineFormData = { ...line, [field]: value };

                if (field === 'line_total') {
                    // Once a total has been typed by hand, stop overwriting it —
                    // silently replacing a figure someone just entered is worse
                    // than leaving an inconsistent row for them to resolve.
                    next.touchedTotal = true;

                    return next;
                }

                if ((field === 'quantity' || field === 'unit_price') && !line.touchedTotal) {
                    const quantity = Number(field === 'quantity' ? value : line.quantity);
                    const price = Number(field === 'unit_price' ? value : line.unit_price);

                    next.line_total = Number.isFinite(quantity) && Number.isFinite(price) && (quantity || price) ? (quantity * price).toFixed(2) : '';
                }

                return next;
            }),
        );
    };

    const removeLine = (index: number) => onChange(lines.filter((_, i) => i !== index));

    if (lines.length === 0) {
        return (
            <div className="space-y-3">
                <p className="text-muted-foreground text-sm">{t('leads.offer_lines.empty', 'No lines yet. Add one, or import them from a PDF.')}</p>
                {!disabled && (
                    <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => onChange([emptyOfferLine()])}>
                        <Plus className="h-4 w-4" />
                        {t('leads.offer_lines.add_line', 'Add line')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Eight columns will not fit the narrower mount points, so the table
                scrolls inside its own container rather than the page. Below md it
                is replaced entirely by the card list further down — no width of
                sideways scrolling makes eight columns usable on a phone. */}
            <div className="hidden overflow-x-auto rounded-lg border md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-12">{t('leads.offer_lines.columns.position', 'No.')}</TableHead>
                            <TableHead className="min-w-[120px]">{t('leads.offer_lines.columns.code', 'Code')}</TableHead>
                            <TableHead className="min-w-[200px]">{t('leads.offer_lines.columns.name', 'Product / service')}</TableHead>
                            <TableHead className="w-24">{t('leads.offer_lines.columns.quantity', 'Qty')}</TableHead>
                            <TableHead className="w-24">{t('leads.offer_lines.columns.unit', 'Unit')}</TableHead>
                            <TableHead className="w-24">{t('leads.offer_lines.columns.discount', 'Discount')}</TableHead>
                            <TableHead className="w-32">{t('leads.offer_lines.columns.unit_price', 'Price excl. VAT')}</TableHead>
                            <TableHead className="w-32">{t('leads.offer_lines.columns.line_total', 'Total excl. VAT')}</TableHead>
                            {!disabled && <TableHead className="w-12" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {lines.map((line, index) => (
                            <TableRow key={index}>
                                <TableCell className="text-muted-foreground text-sm">{index + 1}</TableCell>
                                <TableCell>
                                    <Input
                                        value={line.code}
                                        onChange={(event) => updateLine(index, 'code', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.code', 'Code')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <div className="space-y-1.5">
                                        <Input
                                            value={line.name}
                                            onChange={(event) => updateLine(index, 'name', event.target.value)}
                                            disabled={disabled}
                                            aria-label={t('leads.offer_lines.columns.name', 'Product / service')}
                                        />
                                        {line.warnings && line.warnings.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {line.warnings.map((warning) => (
                                                    <Badge key={warning} variant="outline" className="gap-1 text-xs">
                                                        <AlertTriangle className="h-3 w-3" />
                                                        {t(`leads.offer_pdf.warnings.${warning}`, warning)}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        step="0.001"
                                        value={line.quantity}
                                        onChange={(event) => updateLine(index, 'quantity', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.quantity', 'Qty')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        value={line.unit}
                                        onChange={(event) => updateLine(index, 'unit', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.unit', 'Unit')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        value={line.discount}
                                        onChange={(event) => updateLine(index, 'discount', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.discount', 'Discount')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={line.unit_price}
                                        onChange={(event) => updateLine(index, 'unit_price', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.unit_price', 'Price excl. VAT')}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={line.line_total}
                                        onChange={(event) => updateLine(index, 'line_total', event.target.value)}
                                        disabled={disabled}
                                        aria-label={t('leads.offer_lines.columns.line_total', 'Total excl. VAT')}
                                    />
                                </TableCell>
                                {!disabled && (
                                    <TableCell>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive hover:text-destructive"
                                            onClick={() => removeLine(index)}
                                            aria-label={t('leads.offer_lines.remove_line', 'Remove line')}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile: the row unfolded into a labelled card, so every field stays
                reachable without a horizontal scroll. */}
            <div className="space-y-3 md:hidden">
                {lines.map((line, index) => (
                    <div key={index} className="bg-card space-y-3 rounded-xl border p-3">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground text-xs font-medium">
                                {t('leads.offer_lines.line_number', 'Line :number').replace(':number', String(index + 1))}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold">{formatCurrency(Number(line.line_total) || 0)}</span>
                                {!disabled && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive h-8 w-8 shrink-0"
                                        onClick={() => removeLine(index)}
                                        aria-label={t('leads.offer_lines.remove_line', 'Remove line')}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        {disabled ? (
                            <>
                                <p className="text-sm font-medium">{line.name}</p>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    {(
                                        [
                                            ['code', t('leads.offer_lines.columns.code', 'Code'), line.code],
                                            ['quantity', t('leads.offer_lines.columns.quantity', 'Qty'), line.quantity],
                                            ['unit', t('leads.offer_lines.columns.unit', 'Unit'), line.unit],
                                            ['discount', t('leads.offer_lines.columns.discount', 'Discount'), line.discount],
                                            ['unit_price', t('leads.offer_lines.columns.unit_price', 'Price excl. VAT'), line.unit_price],
                                        ] as const
                                    )
                                        .filter(([, , value]) => value !== '' && value !== null && value !== undefined)
                                        .map(([key, label, value]) => (
                                            <div key={key} className="flex items-baseline justify-between gap-2">
                                                <dt className="text-muted-foreground shrink-0">{label}</dt>
                                                <dd className="truncate font-medium">{value}</dd>
                                            </div>
                                        ))}
                                </dl>
                            </>
                        ) : (
                            <>
                                <div className="space-y-1">
                                    <Label htmlFor={`offer-line-${index}-name`} className="text-muted-foreground text-xs">
                                        {t('leads.offer_lines.columns.name', 'Product / service')}
                                    </Label>
                                    <Input
                                        id={`offer-line-${index}-name`}
                                        value={line.name}
                                        onChange={(event) => updateLine(index, 'name', event.target.value)}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <Label htmlFor={`offer-line-${index}-quantity`} className="text-muted-foreground text-xs">
                                            {t('leads.offer_lines.columns.quantity', 'Qty')}
                                        </Label>
                                        <Input
                                            id={`offer-line-${index}-quantity`}
                                            type="number"
                                            inputMode="decimal"
                                            step="0.001"
                                            value={line.quantity}
                                            onChange={(event) => updateLine(index, 'quantity', event.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor={`offer-line-${index}-unit`} className="text-muted-foreground text-xs">
                                            {t('leads.offer_lines.columns.unit', 'Unit')}
                                        </Label>
                                        <Input
                                            id={`offer-line-${index}-unit`}
                                            value={line.unit}
                                            onChange={(event) => updateLine(index, 'unit', event.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor={`offer-line-${index}-unit_price`} className="text-muted-foreground text-xs">
                                            {t('leads.offer_lines.columns.unit_price', 'Price excl. VAT')}
                                        </Label>
                                        <Input
                                            id={`offer-line-${index}-unit_price`}
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            value={line.unit_price}
                                            onChange={(event) => updateLine(index, 'unit_price', event.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor={`offer-line-${index}-line_total`} className="text-muted-foreground text-xs">
                                            {t('leads.offer_lines.columns.line_total', 'Total excl. VAT')}
                                        </Label>
                                        <Input
                                            id={`offer-line-${index}-line_total`}
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            value={line.line_total}
                                            onChange={(event) => updateLine(index, 'line_total', event.target.value)}
                                        />
                                    </div>
                                </div>

                                {isLineExpanded(index, line) ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label htmlFor={`offer-line-${index}-code`} className="text-muted-foreground text-xs">
                                                {t('leads.offer_lines.columns.code', 'Code')}
                                            </Label>
                                            <Input
                                                id={`offer-line-${index}-code`}
                                                value={line.code}
                                                onChange={(event) => updateLine(index, 'code', event.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor={`offer-line-${index}-discount`} className="text-muted-foreground text-xs">
                                                {t('leads.offer_lines.columns.discount', 'Discount')}
                                            </Label>
                                            <Input
                                                id={`offer-line-${index}-discount`}
                                                inputMode="decimal"
                                                value={line.discount}
                                                onChange={(event) => updateLine(index, 'discount', event.target.value)}
                                            />
                                        </div>
                                    </div>
                                ) : null}

                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto gap-1 p-0"
                                    aria-expanded={isLineExpanded(index, line)}
                                    onClick={() => toggleLine(index, line)}
                                >
                                    <ChevronDown className={`h-3 w-3 transition-transform ${isLineExpanded(index, line) ? 'rotate-180' : ''}`} />
                                    {isLineExpanded(index, line)
                                        ? t('leads.offer_lines.fewer_fields', 'Hide code and discount')
                                        : t('leads.offer_lines.more_fields', 'Code, discount')}
                                </Button>
                            </>
                        )}

                        {line.warnings && line.warnings.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {line.warnings.map((warning) => (
                                    <Badge key={warning} variant="outline" className="gap-1 text-xs">
                                        <AlertTriangle className="h-3 w-3" />
                                        {t(`leads.offer_pdf.warnings.${warning}`, warning)}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {!disabled ? (
                    <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => onChange([...lines, emptyOfferLine()])}>
                        <Plus className="h-4 w-4" />
                        {t('leads.offer_lines.add_line', 'Add line')}
                    </Button>
                ) : (
                    <span />
                )}
                <div className="text-sm">
                    <span className="text-muted-foreground">{t('leads.offer_lines.total', 'Lines total')}: </span>
                    <span className="font-semibold">{formatCurrency(offerLinesTotal(lines))}</span>
                </div>
            </div>
        </div>
    );
}
